import type React from 'react';
import {useEffect} from 'react';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';

// Presence, not the modal's own Escape handler: focus is often on `body`, which never reaches it.
function isModalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

/**
 * Exits annotation mode on Escape, yielding to a menu, an open modal, or the style popover.
 * The coachmark is non-modal, so it answers Escape only while it has focus; anywhere else
 * the mode goes off and takes the coachmark with it.
 */
export function useAnnotationModeEscape(toggleRef: React.RefObject<HTMLButtonElement | null>) {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);

  useEffect(() => {
    if (!isAnnotationModeOn) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || isModalOpen()) {
        return;
      }
      const state = useAnnotationModeStore.getState();
      if (!state.isAnnotationModeOn) {
        return;
      }
      state.toggleAnnotationMode();
      toggleRef.current?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAnnotationModeOn, toggleRef]);
}
